import {useEffect, useRef, useState} from 'react';
import type {HermesState} from './lib/types';
import type {hermesActions} from './lib/store';
import {APP_BASE, Link, navigate} from './router';
import {ErrorNotice, Loading} from './components';
import storagePolicy from '../config/account-storage.json';
import './account-setup.css';

export function AccountSetupPage({state, actions, planId}: {
  state: HermesState; actions: typeof hermesActions; planId: string | null;
}) {
  const accountId = state.session.data?.accountId;
  const requestId = useRef(crypto.randomUUID());
  const [attempt, setAttempt] = useState(0);
  const preparation = state.accountPreparation.data;
  const membership = state.accountBilling.data?.membership;
  const plan = state.billingPlans.data?.plans.find(plan => plan.id === planId);
  const error = state.operation.scope === 'setup' ? state.operation.error : null;
  const busy = state.operation.label !== null;
  const validPlan = planId !== null && /^[0-9a-f-]{36}$/i.test(planId);
  useEffect(() => {
    if (!accountId || !validPlan) return;
    const controller = new AbortController();
    // StrictMode's discarded mount must not submit an operation.
    const timer = window.setTimeout(() => {
      void actions.setupAccount(planId, requestId.current, controller.signal, attempt > 0).then(result => {
        if (controller.signal.aborted) return;
        navigate(result.billing ? `${APP_BASE}/billing?${new URLSearchParams({plan: planId})}`
          : `${APP_BASE}/workspace?${new URLSearchParams({chat: result.sessionId})}`, true);
      }).catch(() => { /* The shared setup operation owns the complete error. */ });
    }, 0);
    return () => {window.clearTimeout(timer); controller.abort();};
  }, [accountId, planId, validPlan, actions, attempt]);

  if (!validPlan) return <section className="account-setup"><p className="eyebrow">Welcome to EightBots</p>
    <h1>First, choose your team’s allowance.</h1><p>Your account is created. Select a plan and we’ll guide you through the rest.</p>
    <Link className="button" href={`${APP_BASE}/subscribe?new_signup=1`}>Choose a plan</Link></section>;

  const serverDone = preparation?.state === 'ready' || (preparation?.phase === 'team' && preparation.state !== 'pending');
  const teamDone = preparation?.state === 'ready';
  const planDone = membership?.planId === planId;
  const current = !serverDone ? 0 : !teamDone ? 1 : !planDone ? 2 : 3;
  const steps = [
    {title: 'Creating your private server', description: `${storagePolicy.limitBytes / 1_000_000_000} GB for your files, projects and conversations. No flat-pack furniture required.`, done: serverDone},
    {title: 'Setting up your AI team', description: 'Preparing eight agents and your first chat. Zero calendar invites.', done: teamDone},
    {title: plan ? `Activating ${plan.name}` : 'Activating your plan', description: 'Adding your selected plan’s credits. No payment is required during early access.', done: planDone},
    {title: 'Opening your workspace', description: 'Connecting your chat and checking that everything is ready for your first idea.', done: false},
  ];
  return <section className="account-setup" aria-labelledby="setup-title">
    <Loading active={busy}/>
    <p className="eyebrow">Account created</p>
    <h1 id="setup-title">Your team is moving in.</h1>
    <p className="setup-intro">We’re preparing your own server space, AI team and credits. First setup can take a minute or two. Your chat opens here when it’s ready.</p>
    <ol className="setup-steps" aria-label="Account setup progress">
      {steps.map((step, index) => <li key={index} data-state={step.done ? 'complete' : current === index ? error ? 'error' : 'current' : 'waiting'}
        aria-current={current === index ? 'step' : undefined}>
        <span className="setup-step-number" aria-hidden="true">{index + 1}</span>
        <div><div className="setup-step-heading"><h2>{step.title}</h2><span>{step.done ? 'Ready' : current === index ? error ? 'Needs attention' : 'In progress' : 'Up next'}</span></div>
          <p>{step.description}</p></div>
      </li>)}
    </ol>
    <div className="setup-status" role="status" aria-live="polite" aria-atomic="true">
      {error ? 'Setup needs your attention. Your account and saved progress are still here.'
        : !preparation ? 'Checking your saved setup progress.'
        : preparation.state === 'pending' ? 'Your server is in the setup queue. We’ll keep this page updated.'
        : `${steps[current]!.title}. You can stay here while we finish.`}
    </div>
    {error && <div className="setup-error"><p>If the connection dropped, we’ll check whether your plan was already activated before continuing. Retrying won’t duplicate your credits or replace your files.</p>
      <ErrorNotice error={error}/><button className="button" type="button" disabled={busy} onClick={() => setAttempt(value => value + 1)}>Check setup and continue</button></div>}
    <p className="setup-return-note">Need to step away? Reopen this page to continue from your saved progress.</p>
    <Link className="text-button" href={`${APP_BASE}/subscribe?new_signup=1`}>Choose a different plan</Link>
  </section>;
}
